'use client';

import React, { FC } from 'react';
import styles from './CtaBanner.module.scss';

import { useInView } from 'react-intersection-observer';
import createAnimationArr from '@/src/lib/helpers/createAnimationArr';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import NextImage from '@/src/components/ui/nextImage/NextImage';
import { ITheme } from '@/src/lib/types/themeInterface';
import ShopifyStoreButton from '@/src/components/ui/shopifyButton/ShopifyStoreButton';

type TPage = {
  theme: ITheme;
};

const heading = 'Create Accent with Underline';
const excerpt =
  'Give your messaging extra impact — use animated underlines to guide attention and add personality to your content.';

const CtaBanner: FC<TPage> = ({ theme }) => {
  const { ref: inViewRef2, inView: inView2 } = useInView({
    triggerOnce: true,
    rootMargin: '-250px 0px 0px',
    threshold: 0,
  });

  return (
    <section className={styles.ctaBanner}>
      <div className={styles.ctaBanner__about}>
        <Wrapper>
          <div className={styles.ctaBanner__head} ref={inViewRef2}>
            <h2 className={`heading__2 ${styles.heading} ${inView2 && styles.animate__heading}`}>
              {createAnimationArr(heading, styles.word__wrapper, 0, 0.01)}
              <svg
                width="258"
                height="36"
                viewBox="0 0 258 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4 12.1405C34.5364 7.88454 125.151 0.649439 243.316 5.75657"
                  stroke="#F698B7"
                  strokeWidth="8"
                  strokeLinecap="round"
                />
                <path
                  d="M70.1864 24.6094C91.8891 19.7938 156.837 9.33486 243.008 6.02345"
                  stroke="#F698B7"
                  strokeWidth="8"
                  strokeLinecap="round"
                />
                <path
                  d="M70.9051 24.5209C100.263 22.1925 142.356 17.7982 253.826 29.2735"
                  stroke="#F698B7"
                  strokeWidth="8"
                  strokeLinecap="round"
                />
              </svg>
            </h2>
            <p className={`excerpt ${styles.excerpt} ${inView2 && styles.animate__excerpt}`}>
              {createAnimationArr(excerpt, styles.word__wrapper, 0.1, 0.01)}
            </p>
            <div className={styles.ctaBanner__buttons}>
              <ShopifyStoreButton isWhite={true} theme={theme} />
            </div>
          </div>
        </Wrapper>
      </div>

      <div className={styles.ctaBanner__gallery}>
        <div className={styles['ctaBanner__gallery-column']}>
          <div
            className={`${styles['ctaBanner__gallery-list']} ${styles['ctaBanner__gallery-list_top']}`}>
            {Array(3)
              .fill(null)
              .map((_, i) => (
                <NextImage
                  key={`left-top-${i}`}
                  src="/images/themes/stockist/banner/left.webp"
                  alt="BannerLeft"
                  fill
                  sizes="(max-width: 480px) 100vw, (max-width: 1280px) 30vw, 50vw"
                  loading="lazy"
                />
              ))}
          </div>
          <div
            className={`${styles['ctaBanner__gallery-list']} ${styles['ctaBanner__gallery-list_top']}`}
            aria-hidden="true">
            {Array(3)
              .fill(null)
              .map((_, i) => (
                <NextImage
                  key={`left-top-hidden-${i}`}
                  src="/images/themes/stockist/banner/left.webp"
                  alt="BannerLeft"
                  fill
                  sizes="(max-width: 480px) 100vw, (max-width: 1280px) 30vw, 50vw"
                  loading="lazy"
                />
              ))}
          </div>
        </div>
        <div
          className={`${styles['ctaBanner__gallery-column']} ${styles['ctaBanner__gallery-column_btm']}`}>
          <div
            className={`${styles['ctaBanner__gallery-list']} ${styles['ctaBanner__gallery-list_btm']}`}>
            {Array(3)
              .fill(null)
              .map((_, i) => (
                <NextImage
                  key={`right-btm-${i}`}
                  src="/images/themes/stockist/banner/right.webp"
                  alt="BannerRight"
                  fill
                  sizes="(max-width: 480px) 100vw, (max-width: 1280px) 30vw, 50vw"
                  loading="lazy"
                />
              ))}
          </div>
          <div
            className={`${styles['ctaBanner__gallery-list']} ${styles['ctaBanner__gallery-list_btm']}`}
            aria-hidden="true">
            {Array(3)
              .fill(null)
              .map((_, i) => (
                <NextImage
                  key={`right-btm-hidden-${i}`}
                  src="/images/themes/stockist/banner/right.webp"
                  alt="BannerRight"
                  fill
                  sizes="(max-width: 480px) 100vw, (max-width: 1280px) 30vw, 50vw"
                  loading="lazy"
                />
              ))}
          </div>
        </div>
      </div>

      <div className={styles.ctaBanner__scroller}>
        <div className={styles['ctaBanner__scroller-column']}>
          <div
            className={`${styles['ctaBanner__scroller-list']} ${styles['ctaBanner__scroller-list_top']}`}>
            {Array(3)
              .fill(null)
              .map((_, i) => (
                <NextImage
                  key={`line1-top-${i}`}
                  src="/images/themes/stockist/banner/line-1.webp"
                  alt="BannerTop"
                  fill
                  sizes="100vw"
                  loading="lazy"
                />
              ))}
          </div>
          <div
            className={`${styles['ctaBanner__scroller-list']} ${styles['ctaBanner__scroller-list_top']}`}
            aria-hidden="true">
            {Array(3)
              .fill(null)
              .map((_, i) => (
                <NextImage
                  key={`line1-top-hidden-${i}`}
                  src="/images/themes/stockist/banner/line-1.webp"
                  alt="BannerTop"
                  fill
                  sizes="100vw"
                  loading="lazy"
                />
              ))}
          </div>
        </div>
        <div
          className={`${styles['ctaBanner__scroller-column']} ${styles['ctaBanner__scroller-column_btm']}`}>
          <div
            className={`${styles['ctaBanner__scroller-list']} ${styles['ctaBanner__scroller-list_btm']}`}>
            {Array(3)
              .fill(null)
              .map((_, i) => (
                <NextImage
                  key={`line2-btm-${i}`}
                  src="/images/themes/stockist/banner/line-2.webp"
                  alt="BannerBtm"
                  fill
                  sizes="100vw"
                  loading="lazy"
                />
              ))}
          </div>
          <div
            className={`${styles['ctaBanner__scroller-list']} ${styles['ctaBanner__scroller-list_btm']}`}
            aria-hidden="true">
            {Array(3)
              .fill(null)
              .map((_, i) => (
                <NextImage
                  key={`line2-btm-hidden-${i}`}
                  src="/images/themes/stockist/banner/line-2.webp"
                  alt="BannerBtm"
                  fill
                  loading="lazy"
                  sizes="100vw"
                />
              ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CtaBanner;
